import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useWorkingOverlay } from './workingOverlay';
import './workingMobile.css';

interface Props {
  title: string;
  avatar?: ReactNode;
  subtitle?: string;
  label: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  flow?: boolean;
  chat?: boolean;
  usageTool?: 'study-tutor';
}
export function WorkingSheet({
  title,
  avatar,
  subtitle,
  label,
  onClose,
  children,
  footer,
  flow = false,
  chat = false,
  usageTool,
}: Props) {
  const { fullScreen, portalTarget, sheetStyle, shortViewport } = useWorkingOverlay();
  const compact = useRef(shortViewport);
  compact.current = shortViewport;
  const toolSheet = sheetStyle !== undefined;
  const sheet = useRef<HTMLElement>(null);
  const swipe = useRef<number | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useLayoutEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    (
      sheet.current?.querySelector<HTMLTextAreaElement>('textarea:not([readonly])') ??
      sheet.current?.querySelector<HTMLButtonElement>('[data-sheet-close]')
    )?.focus();
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        close.current();
      }
      const modalQuery = toolSheet
        ? '(max-width: 1023px), (max-height: 500px)'
        : '(max-width: 767px), (max-width: 1023px) and (max-height: 500px)';
      if (event.key !== 'Tab' || !(compact.current || window.matchMedia?.(modalQuery).matches))
        return;
      const nodes = Array.from(
        sheet.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), textarea:not(:disabled), select:not(:disabled), input:not(:disabled), summary',
        ) ?? [],
      ).filter((node) => node.getClientRects().length > 0);
      const first = nodes[0],
        last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', keyboard, true);
    return () => {
      document.removeEventListener('keydown', keyboard, true);
      opener?.focus();
    };
  }, [portalTarget, toolSheet]);
  const content = (
    <>
      {fullScreen && (
        <button
          type="button"
          className={`working-sheet-backdrop${toolSheet ? ' working-tool-backdrop' : ''}${shortViewport ? ' working-tool-compact' : ''}`}
          style={sheetStyle}
          onClick={onClose}
          aria-label="Dismiss panel and return to working"
        />
      )}
      <section
        ref={sheet}
        data-study-tool={usageTool}
        data-study-semantic={usageTool ? 'tutor' : undefined}
        aria-label={label}
        style={sheetStyle}
        className={
          fullScreen
            ? `working-sheet${chat ? ' working-chat-sheet' : ''}${toolSheet ? ' working-tool-sheet' : ''}${shortViewport ? ' working-tool-compact' : ''}`
            : flow
              ? 'working-sheet-flow'
              : 'working-sheet-inline'
        }
      >
        <button
          type="button"
          className="working-sheet-grip"
          aria-label="Close panel with handle"
          onClick={onClose}
          onPointerDown={(event) => {
            swipe.current = event.clientY;
            event.currentTarget.setPointerCapture?.(event.pointerId);
          }}
          onPointerUp={(event) => {
            if (swipe.current !== null && event.clientY - swipe.current > 50) onClose();
            swipe.current = null;
          }}
          onPointerCancel={() => {
            swipe.current = null;
          }}
        >
          <span aria-hidden="true" />
        </button>
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-hairline px-5 pb-3 pt-2 md:pt-3">
          <div className="flex min-w-0 items-center gap-3">
            {avatar}
            <div className="min-w-0">
              <h4 className="text-lg font-extrabold text-ink">{title}</h4>
              {subtitle && <p className="mt-0.5 text-xs font-bold text-slate2">{subtitle}</p>}
            </div>
          </div>
          <button
            data-sheet-close
            type="button"
            aria-label={`Close ${label}`}
            onClick={onClose}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface text-ink"
          >
            <X size={20} />
          </button>
        </header>
        <div className="working-sheet-body">{children}</div>
        {footer && <footer className="working-sheet-footer">{footer}</footer>}
      </section>
    </>
  );
  return portalTarget ? createPortal(content, portalTarget) : content;
}
